/**
 * 会話の進行管理。
 *
 * 「いまどの行を表示しているか」「選択肢は出せるか」だけを持ち、
 * 描画は ui/dialogue.ts に任せる。
 */

import type { Dialogue, DialogueChoice, DialogueLine } from '../types/scenario';
import type { ScenarioEngine } from './engine';
import { evaluateCondition } from './conditions';

export interface DialogueView {
  dialogueId: string;
  line: DialogueLine;
  index: number;
  total: number;
  /** 最終行に到達し、選択肢を出す段階か */
  choices: DialogueChoice[];
  isLast: boolean;
}

export class DialogueRunner {
  private queue: string[] = [];
  private current: Dialogue | null = null;
  private lineIndex = 0;

  constructor(
    private readonly engine: ScenarioEngine,
    private readonly onUpdate: (view: DialogueView | null) => void,
  ) {}

  get isActive(): boolean {
    return this.current !== null;
  }

  /** 会話を開始する（すでに再生中なら順番待ちに積む） */
  start(dialogueId: string): void {
    if (this.current) {
      this.queue.push(dialogueId);
      return;
    }
    const dialogue = this.engine.getDialogue(dialogueId);
    if (!dialogue) {
      console.warn(`[dialogue] ${dialogueId} が見つかりません`);
      this.playNextQueued();
      return;
    }
    if (dialogue.once && this.engine.hasSeenDialogue(dialogueId)) {
      this.playNextQueued();
      return;
    }
    this.current = dialogue;
    this.lineIndex = -1;
    this.advance();
  }

  /** 次の行へ進む。最終行の後は終了処理を行う */
  advance(): void {
    const dialogue = this.current;
    if (!dialogue) return;

    this.lineIndex += 1;
    if (this.lineIndex >= dialogue.lines.length) {
      const choices = this.visibleChoices(dialogue);
      if (choices.length > 0) {
        // 選択肢は最終行と一緒に表示しているので、ここに来たら閉じてよい
        this.lineIndex = dialogue.lines.length - 1;
        this.emit();
        return;
      }
      this.finish();
      return;
    }

    const line = dialogue.lines[this.lineIndex];
    this.engine.runEffects(line.effects);
    for (const term of line.terms ?? []) this.engine.markTermSeen(term);
    this.emit();
  }

  /** 選択肢を選ぶ */
  choose(index: number): void {
    const dialogue = this.current;
    if (!dialogue) return;
    const choices = this.visibleChoices(dialogue);
    const choice = choices[index];
    if (!choice) return;

    this.engine.runEffects(choice.effects);
    const next = choice.nextDialogueId;
    this.finish(next ? [next] : []);
  }

  /** 会話を強制終了する（設定画面を開いた時など） */
  cancel(): void {
    this.current = null;
    this.queue = [];
    this.onUpdate(null);
  }

  private visibleChoices(dialogue: Dialogue): DialogueChoice[] {
    return (dialogue.choices ?? []).filter((choice) =>
      evaluateCondition(choice.condition, this.engine.state, this.engine.world),
    );
  }

  private finish(extraQueue: string[] = []): void {
    const dialogue = this.current;
    this.current = null;
    if (dialogue) {
      this.engine.markDialogueSeen(dialogue.id);
      this.engine.runEffects(dialogue.onEnd);
    }
    this.queue.push(...extraQueue);
    this.playNextQueued();
  }

  private playNextQueued(): void {
    const next = this.queue.shift();
    if (next) {
      this.start(next);
    } else {
      this.onUpdate(null);
    }
  }

  private emit(): void {
    const dialogue = this.current;
    if (!dialogue) {
      this.onUpdate(null);
      return;
    }
    const isLast = this.lineIndex >= dialogue.lines.length - 1;
    this.onUpdate({
      dialogueId: dialogue.id,
      line: dialogue.lines[this.lineIndex],
      index: this.lineIndex,
      total: dialogue.lines.length,
      choices: isLast ? this.visibleChoices(dialogue) : [],
      isLast,
    });
  }
}
